import React from "react";
import { FaSpinner, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import {
  buttonStyleSm2,
  buttonStyleSecondarySm,
} from "../../../help/Utils";
import type { MissingCategoryNode } from "../../../utils/missingCategoriesTree";

export type SubmitConfirmationState = "confirm" | "missing";

export interface SubmitConfirmationModalProps {
  open: boolean;
  /** Drives the modal copy and primary action. */
  state: SubmitConfirmationState;
  /**
   * Categories (with their nested children) that still have at least
   * one unanswered visible question. Only used when state === "missing".
   * Computed by `buildMissingCategoriesTree`.
   */
  missingCategories?: MissingCategoryNode[];
  /** True while completeForm is in flight (state === "confirm"). */
  isSubmitting?: boolean;
  onClose: () => void;
  /** Called when the user confirms submission. */
  onConfirm: () => void;
  /**
   * Called when the user clicks a section in the "missing" list. The
   * parent closes the modal and navigates to the section, optionally
   * scrolling to the first missing question if one is available.
   */
  onMissingCategoryClick?: (
    categoryId: number,
    firstMissingQuestionId: number | null,
  ) => void;
}

/**
 * Recursive renderer for the pruned missing-categories tree.
 *
 * Each node is a clickable button that the parent can wire to navigate
 * to that section. Nested categories are indented to mirror the form's
 * own category tree, so the user's mental model lines up with what
 * they'll see in the sidebar.
 */
function MissingCategoryList({
  nodes,
  depth = 0,
  onCategoryClick,
}: {
  nodes: MissingCategoryNode[];
  depth?: number;
  onCategoryClick?: SubmitConfirmationModalProps["onMissingCategoryClick"];
}) {
  if (nodes.length === 0) return null;
  return (
    <ul
      className="space-y-1.5 text-sm"
      style={{
        paddingLeft: depth === 0 ? 0 : "20px",
        listStyle: depth === 0 ? "none" : "disc",
      }}
    >
      {nodes.map((node) => {
        const isLeaf = node.subcategories.length === 0;
        return (
          <li key={node.id}>
            <button
              type="button"
              onClick={() =>
                onCategoryClick?.(node.id, node.firstMissingQuestionId)
              }
              className="text-left hover:underline focus:underline"
              style={{
                color: isLeaf ? "#E76F51" : "#1B365D",
                fontWeight: depth === 0 ? 600 : 500,
              }}
            >
              {node.name}
            </button>
            {node.subcategories.length > 0 && (
              <MissingCategoryList
                nodes={node.subcategories}
                depth={depth + 1}
                onCategoryClick={onCategoryClick}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Lightweight branded modal shown the moment the applicant marks a form
 * complete from the last section.
 *
 * Replaces the persistent SubmitFormBar that used to live above every
 * section. The form is editable until the moment of explicit
 * confirmation. Package-level review submission happens later from the
 * application dashboard.
 *
 *  - `state === "confirm"` ⇒ readiness passed; ask for explicit go/no-go.
 *  - `state === "missing"` ⇒ readiness failed; surface the GAPS as a
 *    nested list of category names. Clicking a category navigates the
 *    user there. Part / item numbers are deliberately NOT shown — they
 *    live in the backend payload for PDF mapping, not user copy.
 *
 * Inline styles are intentional here so the modal is portable into any
 * route that wants it without depending on the surrounding stylesheet.
 */
const SubmitConfirmationModal: React.FC<SubmitConfirmationModalProps> = ({
  open,
  state,
  missingCategories = [],
  isSubmitting = false,
  onClose,
  onConfirm,
  onMissingCategoryClick,
}) => {
  if (!open) return null;

  const isConfirm = state === "confirm";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="submit-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={(e) => {
        // Click outside the card dismisses (only when not submitting).
        if (!isSubmitting && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        style={{ fontFamily: "Nunito, sans-serif" }}
      >
        <div className="flex items-start gap-3 px-6 pt-6">
          {isConfirm ? (
            <FaCheckCircle
              className="mt-1 shrink-0"
              style={{ color: "#80EFD9", fontSize: "22px" }}
              aria-hidden
            />
          ) : (
            <FaExclamationTriangle
              className="mt-1 shrink-0"
              style={{ color: "#F4A261", fontSize: "20px" }}
              aria-hidden
            />
          )}
          <div className="min-w-0 flex-1">
            <h2
              id="submit-confirm-title"
              className="text-[#1B365D] text-xl font-semibold"
            >
              {isConfirm
                ? "Submit form for review?"
                : "These sections still need attention"}
            </h2>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              {isConfirm
                ? "This marks this form complete. After this, return to your application dashboard to upload the required documents and continue the application. Your full application is submitted to LGV for review only when you click Submit for review from the dashboard."
                : "Please finish answering the questions in the sections below before submitting. Click any section to jump straight to it."}
            </p>
          </div>
        </div>

        {!isConfirm && missingCategories.length > 0 && (
          <div className="px-6 mt-4 max-h-[260px] overflow-auto border-t border-gray-100 pt-3">
            <MissingCategoryList
              nodes={missingCategories}
              onCategoryClick={onMissingCategoryClick}
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-6 py-5 mt-4 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="min-h-[44px] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            style={buttonStyleSecondarySm}
          >
            {isConfirm ? "Cancel" : "Close"}
          </button>
          {isConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              className="min-w-[180px] min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={buttonStyleSm2}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving…
                </>
              ) : (
                "Mark form complete"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmitConfirmationModal;
